import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  styled,
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import rehypeSlug from 'rehype-slug';
import remarkGfm from 'remark-gfm';
import { CloseIcon } from '../../common/icons';

type Props = {
  open: boolean;
  onClose: () => void;
  title: string;
  content: string;
};

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  '& img': {
    maxWidth: '100%',
    height: 'auto',
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  '& table': {
    borderCollapse: 'collapse',
    width: '100%',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  '& th, & td': {
    border: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1),
    textAlign: 'left',
  },
  '& th': {
    backgroundColor: theme.palette.grey[100],
    fontWeight: 600,
  },
  '& pre': {
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    overflow: 'auto',
  },
  '& code': {
    backgroundColor: theme.palette.grey[100],
    padding: '2px 6px',
    borderRadius: 4,
    fontSize: '0.875em',
  },
  '& pre code': {
    padding: 0,
    backgroundColor: 'transparent',
  },
  '& blockquote': {
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    margin: theme.spacing(2, 0),
    padding: theme.spacing(1, 2),
    backgroundColor: theme.palette.grey[50],
  },
  '& h1, & h2, & h3': {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(1),
  },
  '& hr': {
    margin: theme.spacing(3, 0),
    border: 'none',
    borderTop: `1px solid ${theme.palette.divider}`,
  },
}));

/**
 * Renders a dialog that displays Markdown documentation content.
 * @param open
 * @param onClose
 * @param title
 * @param content
 */
const HelpDocDialog = ({ open, onClose, title, content }: Props) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='lg'
      fullWidth
      scroll='paper'
      slotProps={{ paper: { sx: { maxHeight: '80vh' } } }}
    >
      <DialogTitle>
        <Box display='flex' justifyContent='space-between' alignItems='center'>
          {title}
          <IconButton onClick={onClose} size='small'>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <StyledDialogContent dividers>
        <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSlug]}>
          {content}
        </ReactMarkdown>
      </StyledDialogContent>
    </Dialog>
  );
};

export default HelpDocDialog;
